// src/pages/UserManagement/modals/CreateUserModal.tsx
// Create new user modal with Formik validation

import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
  Alert,
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Department, Role } from '../types';
import { VN_MOBILE_PHONE_REGEX, VN_MOBILE_PHONE_ERROR_MESSAGE } from 'helpers/phoneValidation';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { useScreenText } from 'helpers/screenText';

interface CreateUserModalProps {
  isOpen: boolean;
  toggle: () => void;
  departments: Department[];
  roles: Role[];
  onSubmit: (values: any) => Promise<void>;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  toggle,
  departments,
  roles,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  // Text hiển thị lấy từ src/locales/screens.json (mục "user_management" / "common")
  const st = useScreenText('user_management');
  const common = useScreenText('common');

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      phoneNumber: '',
      departmentId: '',
      roleId: '',
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required(st('validation_name_required'))
        .min(2, st('validation_name_min'))
        .max(255, st('validation_name_max')),
      username: Yup.string()
        .required(st('validation_username_required'))
        .min(3, st('validation_username_min'))
        .matches(
          /^[a-zA-Z0-9._]+$/,
          st('validation_username_format')
        ),
      email: Yup.string()
        .email(st('validation_email_format'))
        .required(st('validation_email_required')),
      password: Yup.string()
        .required(st('validation_password_required'))
        .min(8, st('validation_password_strength'))
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
          st('validation_password_strength')
        ),
      confirmPassword: Yup.string()
        .required(st('validation_confirm_password_required'))
        .oneOf([Yup.ref('password')], st('validation_confirm_password_match')),
      phoneNumber: Yup.string()
        .required(st('validation_phone_required'))
        .matches(VN_MOBILE_PHONE_REGEX, VN_MOBILE_PHONE_ERROR_MESSAGE),
      roleId: Yup.string().required(st('validation_role_required')),
    }),
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  const handleClose = () => {
    validation.resetForm();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>
        {st('create_modal_title')}
      </ModalHeader>
      <Form onSubmit={validation.handleSubmit} autoComplete="off">
        <ModalBody>
          <Row>
            {/* Name */}
            <Col md={12}>
              <FormGroup>
                <Label for="name">{st('name_label')} <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  placeholder={st('name_placeholder')}
                  maxLength={255}
                  value={validation.values.name}
                  data-action-code="create-input-name"
                  data-action-name="Tên người dùng (Thêm mới)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "create-input-name")}
                  invalid={
                    validation.touched.name && validation.errors.name
                      ? true
                      : false
                  }
                />
                <div className="text-end">
                  <small className="text-muted">{validation.values.name.length}/255</small>
                </div>
                {validation.touched.name && validation.errors.name ? (
                  <FormFeedback type="invalid">
                    {validation.errors.name}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Username */}
            <Col md={12}>
              <FormGroup>
                <Label for="username">{st('username_label')} <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="username"
                  name="username"
                  autoComplete="off"
                  placeholder={st('username_placeholder')}
                  value={validation.values.username}
                  data-action-code="create-input-username"
                  data-action-name="Tên đăng nhập (Thêm mới)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "create-input-username")}
                  invalid={
                    validation.touched.username && validation.errors.username
                      ? true
                      : false
                  }
                />
                {validation.touched.username && validation.errors.username ? (
                  <FormFeedback type="invalid">
                    {validation.errors.username}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Email */}
            <Col md={6}>
              <FormGroup>
                <Label for="email">{st('email_label')} <span className="text-danger">*</span></Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  placeholder={st('email_placeholder')}
                  value={validation.values.email}
                  data-action-code="create-input-email"
                  data-action-name="Email (Thêm mới)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "create-input-email")}
                  invalid={
                    validation.touched.email && validation.errors.email
                      ? true
                      : false
                  }
                />
                {validation.touched.email && validation.errors.email ? (
                  <FormFeedback type="invalid">
                    {validation.errors.email}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Phone Number */}
            <Col md={6}>
              <FormGroup>
                <Label for="phoneNumber">{st('phone_number_label')} <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  id="phoneNumber"
                  name="phoneNumber"
                  placeholder={st('phone_number_placeholder')}
                  value={validation.values.phoneNumber}
                  data-action-code="create-input-phone-number"
                  data-action-name="Số điện thoại (Thêm mới)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "create-input-phone-number")}
                  invalid={
                    validation.touched.phoneNumber &&
                    validation.errors.phoneNumber
                      ? true
                      : false
                  }
                />
                {validation.touched.phoneNumber &&
                validation.errors.phoneNumber ? (
                  <FormFeedback type="invalid">
                    {validation.errors.phoneNumber}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Password */}
            <Col md={6}>
              <FormGroup>
                <Label for="password">{st('password_label')} <span className="text-danger">*</span></Label>
                <Input
                  type="password"
                  id="password"
                  name="password"
                  autoComplete="new-password"
                  placeholder={st('password_placeholder')}
                  value={validation.values.password}
                  data-action-code="create-input-password"
                  data-action-name="Mật khẩu (Thêm mới)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "create-input-password")}
                  invalid={
                    validation.touched.password && validation.errors.password && !validation.values.password
                      ? true
                      : false
                  }
                />
                {validation.touched.password && validation.errors.password ? (
                  validation.values.password ? (
                    <Alert color="warning" className="py-1 px-2 mt-1 mb-0 small">
                      <i className="ri-error-warning-line me-1"></i>
                      {validation.errors.password}
                    </Alert>
                  ) : (
                    <FormFeedback type="invalid">
                      {validation.errors.password}
                    </FormFeedback>
                  )
                ) : null}
              </FormGroup>
            </Col>

            {/* Confirm Password */}
            <Col md={6}>
              <FormGroup>
                <Label for="confirmPassword">{st('confirm_password_label')} <span className="text-danger">*</span></Label>
                <Input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  autoComplete="new-password"
                  placeholder={st('confirm_password_placeholder')}
                  value={validation.values.confirmPassword}
                  data-action-code="create-input-confirm-password"
                  data-action-name="Xác nhận mật khẩu (Thêm mới)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "create-input-confirm-password")}
                  invalid={
                    validation.touched.confirmPassword &&
                    validation.errors.confirmPassword
                      ? true
                      : false
                  }
                />
                {validation.touched.confirmPassword &&
                validation.errors.confirmPassword ? (
                  <FormFeedback type="invalid">
                    {validation.errors.confirmPassword}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>

            {/* Department */}
            <Col md={6}>
              <FormGroup>
                <Label for="departmentId">{st('department_label')}</Label>
                <Input
                  type="select"
                  id="departmentId"
                  name="departmentId"
                  value={validation.values.departmentId}
                  data-action-code="create-input-department"
                  data-action-name="Phòng ban (Thêm mới)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "create-input-department")}
                >
                  <option value="">{st('department_placeholder')}</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            {/* Role */}
            <Col md={6}>
              <FormGroup>
                <Label for="roleId">{st('role_label')} <span className="text-danger">*</span></Label>
                <Input
                  type="select"
                  id="roleId"
                  name="roleId"
                  value={validation.values.roleId}
                  data-action-code="create-input-role"
                  data-action-name="Vai trò (Thêm mới)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  disabled={!PageActionPermission.canEnable(location.pathname, "create-input-role")}
                  invalid={
                    validation.touched.roleId && validation.errors.roleId
                      ? true
                      : false
                  }
                >
                  <option value="">{st('role_placeholder')}</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </Input>
                {validation.touched.roleId && validation.errors.roleId ? (
                  <FormFeedback type="invalid">
                    {validation.errors.roleId}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleClose}>
            {common('cancel_button_alt')}
          </Button>
          {PageActionPermission.canDisplay(location.pathname, "button-create-submit") && (
            <Button
              color="primary"
              type="submit"
              disabled={validation.isSubmitting}
              data-action-code="button-create-submit"
              data-action-name="Tạo người dùng"
            >
              <i className="ri-save-line me-1"></i>
              {validation.isSubmitting ? st('creating_label') : st('create_button')}
            </Button>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreateUserModal;
